import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { DashboardLayout } from "@/components/dashboard/dashboard-layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Calendar,
  CheckCircle,
  Clock,
  FileText,
  Users,
  Loader2,
  Plus,
  Edit,
  Trash2,
  AlertTriangle,
  ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { AddOnboardingTaskSheet } from "./AddOnboardingTaskSheet";
import { EditOnboardingTaskSheet } from "./EditOnboardingTaskSheet";
import EditClientOnboardingSheet from "./EditClientOnboardingSheet";
import AddOnboardingContactSheet from "./AddOnboardingContactSheet";
import EditOnboardingContactSheet from "./EditOnboardingContactSheet";
import { OnboardingDocumentUploader } from "./OnboardingDocumentUploader";
import { OnboardingAnalyticsDashboard } from "./OnboardingAnalyticsDashboard";

export default function OnboardingView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("overview");

  // Sheet states
  const [isAddTaskSheetOpen, setIsAddTaskSheetOpen] = useState(false);
  const [isEditTaskSheetOpen, setIsEditTaskSheetOpen] = useState(false);
  const [isEditOnboardingSheetOpen, setIsEditOnboardingSheetOpen] = useState(false);
  const [isAddContactSheetOpen, setIsAddContactSheetOpen] = useState(false);
  const [isEditContactSheetOpen, setIsEditContactSheetOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any>(null);
  const [selectedContact, setSelectedContact] = useState<any>(null);

  // Loading and confirmation states
  const [taskStatusUpdating, setTaskStatusUpdating] = useState<string | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState<any>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Fetch real onboarding data
  const onboarding = useQuery(api.clientOnboarding.getById, {
    id: id as Id<"clientOnboarding">,
  });

  // Fetch real tasks data
  const tasks =
    useQuery(api.onboardingTasks.getTasksByOnboarding, {
      onboardingId: id as Id<"clientOnboarding">,
    }) || [];

  // Fetch real documents data
  const documents =
    useQuery(api.onboardingDocuments.getDocumentsByOnboarding, {
      onboardingId: id as Id<"clientOnboarding">,
    }) || [];

  // Fetch real contacts data
  const contacts =
    useQuery(api.onboardingContacts.getContactsByOnboarding, {
      onboardingId: id as Id<"clientOnboarding">,
    }) || [];

  // Mutations
  const updateTaskStatus = useMutation(api.onboardingTasks.updateTaskStatus);
  const deleteTask = useMutation(api.onboardingTasks.deleteTask);
  const deleteContact = useMutation(api.onboardingContacts.deleteContact);

  const isLoading = onboarding === undefined;

  // Calculate days remaining
  const daysRemaining = onboarding
    ? (() => {
        const dueDate = new Date(onboarding.dueDate);
        const today = new Date();
        const timeDiff = dueDate.getTime() - today.getTime();
        return onboarding.status === "completed"
          ? 0
          : Math.max(0, Math.ceil(timeDiff / (1000 * 3600 * 24)));
      })()
    : 0;

  // Handle task status change with optimistic updates
  const handleTaskStatusChange = async (taskId: string, completed: boolean) => {
    setTaskStatusUpdating(taskId);

    try {
      await updateTaskStatus({
        id: taskId as Id<"onboardingTasks">,
        status: completed ? "completed" : "not-started",
      });

      toast.success(completed ? "Task marked as completed!" : "Task marked as incomplete", {
        description: "Progress has been updated automatically.",
      });
    } catch (error) {
      console.error("Error updating task status:", error);
      toast.error("Failed to update task status", {
        description: "Please try again or refresh the page.",
      });
    } finally {
      setTaskStatusUpdating(null);
    }
  };

  // Handle task edit
  const handleEditTask = (task: any) => {
    setSelectedTask(task);
    setIsEditTaskSheetOpen(true);
  };

  // Handle task deletion
  const handleDeleteTask = (task: any) => {
    setTaskToDelete(task);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteTask = async () => {
    if (!taskToDelete) return;

    setIsDeleting(true);
    try {
      await deleteTask({ id: taskToDelete._id as Id<"onboardingTasks"> });
      toast.success("Task deleted successfully", {
        description: "Progress has been recalculated automatically.",
      });
    } catch (error) {
      console.error("Error deleting task:", error);
      toast.error("Failed to delete task", {
        description: "Please try again or refresh the page.",
      });
    } finally {
      setDeleteConfirmOpen(false);
      setTaskToDelete(null);
      setIsDeleting(false);
    }
  };

  // Handle contact edit
  const handleEditContact = (contact: any) => {
    setSelectedContact(contact);
    setIsEditContactSheetOpen(true);
  };

  // Handle contact deletion
  const handleDeleteContact = async (contact: any) => {
    if (contact.type !== "additional") {
      toast.error("Cannot delete client or location contacts");
      return;
    }

    try {
      await deleteContact({ id: contact._id });
      toast.success("Contact deleted successfully");
    } catch (error) {
      console.error("Error deleting contact:", error);
      toast.error("Failed to delete contact");
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "Not Started":
      case "not-started":
        return "bg-gray-200 text-gray-800";
      case "In Progress":
      case "in-progress":
        return "bg-blue-100 text-blue-800";
      case "Completed":
      case "completed":
        return "bg-green-100 text-green-800";
      default:
        return "";
    }
  };

  const getTaskStatusBadgeColor = (status: string) => {
    switch (status) {
      case "not-started":
        return "bg-gray-200 text-gray-800";
      case "in-progress":
        return "bg-blue-100 text-blue-800";
      case "completed":
        return "bg-green-100 text-green-800";
      default:
        return "";
    }
  };

  const getTaskStatusDisplay = (status: string) => {
    switch (status) {
      case "not-started":
        return "Not Started";
      case "in-progress":
        return "In Progress";
      case "completed":
        return "Completed";
      default:
        return status;
    }
  };

  const getPriorityBadgeColor = (priority: string) => {
    switch (priority) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout activeTab="clients">
        <div className="flex h-64 items-center justify-center">
          <div className="flex items-center">
            <Loader2 className="mr-2 h-6 w-6 animate-spin" />
            Loading onboarding details...
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!onboarding) {
    return (
      <DashboardLayout activeTab="clients">
        <div className="flex h-64 items-center justify-center">
          <div className="text-center">
            <h2 className="mb-2 text-xl font-semibold">Onboarding Not Found</h2>
            <p className="mb-4 text-gray-600">
              The onboarding record you're looking for doesn't exist.
            </p>
            <Button onClick={() => navigate("/clients/onboarding")}>Back to Onboarding List</Button>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  const statusDisplay =
    onboarding.status === "not-started"
      ? "Not Started"
      : onboarding.status === "in-progress"
        ? "In Progress"
        : "Completed";

  return (
    <DashboardLayout activeTab="clients">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="mb-2 flex items-center gap-3">
              <Button
                variant="ghost"
                size="sm"
                className="gap-1 pl-0"
                onClick={() => navigate("/clients/onboarding")}
              >
                <ArrowLeft className="h-4 w-4" /> Back to Location Onboarding
              </Button>
            </div>
            <div className="flex items-center space-x-2">
              <h1 className="text-2xl font-bold tracking-tight">
                {onboarding.serviceLocationName || "Service Location"} Onboarding
              </h1>
              <Badge className={getStatusBadgeColor(statusDisplay)}>{statusDisplay}</Badge>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button className="gap-2" onClick={() => setIsEditOnboardingSheetOpen(true)}>
              <Edit className="h-4 w-4" /> Edit Onboarding
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6">
          <Card className="col-span-2">
            <CardHeader>
              <CardTitle>Onboarding Progress</CardTitle>
              <CardDescription>
                {onboarding.progress}% complete - {daysRemaining} days remaining
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Progress value={onboarding.progress} className="mb-6 h-2" />

              <Tabs value={activeTab} onValueChange={setActiveTab}>
                <TabsList className="grid w-full grid-cols-4">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  <TabsTrigger value="tasks">Tasks</TabsTrigger>
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                  <TabsTrigger value="analytics">Analytics</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-4 pt-4">
                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-medium">Service Location Information</h3>
                      <Separator className="my-2" />
                      <dl className="grid grid-cols-2 gap-4 pt-2">
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Service Location</dt>
                          <dd>{onboarding.serviceLocationName || "Location Required"}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Client</dt>
                          <dd>{onboarding.businessName}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Start Date</dt>
                          <dd>{onboarding.startDate}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Due Date</dt>
                          <dd>{onboarding.dueDate}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Assigned To</dt>
                          <dd>{onboarding.assignedUserName}</dd>
                        </div>
                        <div>
                          <dt className="text-sm font-medium text-gray-500">Status</dt>
                          <dd>{statusDisplay}</dd>
                        </div>
                      </dl>
                    </div>

                    <div>
                      <h3 className="text-lg font-medium">Notes</h3>
                      <Separator className="my-2" />
                      <p className="text-gray-700">{onboarding.notes || "No notes added yet."}</p>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="tasks" className="space-y-4 pt-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium">Onboarding Tasks</h3>
                    <Button size="sm" onClick={() => setIsAddTaskSheetOpen(true)} className="gap-2">
                      <Plus className="h-4 w-4" />
                      Add Task
                    </Button>
                  </div>
                  <Separator className="my-2" />
                  {tasks.length === 0 ? (
                    <div className="py-8 text-center">
                      <Clock className="mx-auto mb-4 h-12 w-12 text-gray-400" />
                      <h3 className="mb-2 text-lg font-medium">No tasks yet</h3>
                      <p className="mb-4 text-gray-500">
                        Create tasks to track the onboarding progress
                      </p>
                      <Button onClick={() => setIsAddTaskSheetOpen(true)}>
                        <Plus className="mr-2 h-4 w-4" />
                        Add First Task
                      </Button>
                    </div>
                  ) : (
                    <ul className="space-y-2">
                      {tasks.map((task: any) => (
                        <li
                          key={task._id}
                          className="flex items-center justify-between rounded-md border p-3 hover:bg-gray-50"
                        >
                          <div className="flex items-center space-x-3">
                            <Checkbox
                              checked={task.status === "completed"}
                              onCheckedChange={(checked) =>
                                handleTaskStatusChange(task._id, checked as boolean)
                              }
                              disabled={taskStatusUpdating === task._id}
                            />
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <p
                                  className={`cursor-pointer font-medium hover:text-blue-600 ${
                                    task.status === "completed" ? "text-gray-500 line-through" : ""
                                  }`}
                                  onClick={() => handleEditTask(task)}
                                >
                                  {task.title}
                                </p>
                                {task.priority && (
                                  <Badge
                                    variant="outline"
                                    className={getPriorityBadgeColor(task.priority)}
                                  >
                                    {task.priority}
                                  </Badge>
                                )}
                                {taskStatusUpdating === task._id && (
                                  <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
                                )}
                              </div>
                              <div className="mt-1 flex items-center gap-2 text-sm text-gray-500">
                                <span>Assigned to: {task.assignedUserName}</span>
                                <span>•</span>
                                <span>Due: {task.dueDate}</span>
                                {task.description && (
                                  <>
                                    <span>•</span>
                                    <span className="max-w-xs truncate">{task.description}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <Badge className={getTaskStatusBadgeColor(task.status)}>
                              {getTaskStatusDisplay(task.status)}
                            </Badge>
                            <Button variant="ghost" size="sm" onClick={() => handleEditTask(task)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteTask(task)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </TabsContent>

                <TabsContent value="documents" className="space-y-4 pt-4">
                  <OnboardingDocumentUploader onboardingId={id as Id<"clientOnboarding">} />
                </TabsContent>

                <TabsContent value="analytics" className="space-y-4 pt-4">
                  <OnboardingAnalyticsDashboard onboardingId={id as Id<"clientOnboarding">} />
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          <div className="space-y-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle>Key Client Contacts</CardTitle>
                <Button size="sm" onClick={() => setIsAddContactSheetOpen(true)} className="gap-2">
                  <Plus className="h-4 w-4" />
                  Add Contact
                </Button>
              </CardHeader>
              <CardContent>
                {contacts.length === 0 ? (
                  <div className="py-4 text-center">
                    <Users className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                    <p className="text-sm text-gray-500">No contacts found</p>
                  </div>
                ) : (
                  <ul className="space-y-3">
                    {contacts.map((contact: any) => (
                      <li
                        key={contact._id}
                        className="flex items-center justify-between rounded-md border p-3"
                      >
                        <div className="flex items-center space-x-3">
                          <div
                            className={`rounded-full p-2 ${
                              contact.type === "client"
                                ? "bg-blue-100"
                                : contact.type === "location"
                                  ? "bg-green-100"
                                  : "bg-gray-100"
                            }`}
                          >
                            <Users
                              className={`h-4 w-4 ${
                                contact.type === "client"
                                  ? "text-blue-700"
                                  : contact.type === "location"
                                    ? "text-green-700"
                                    : "text-gray-700"
                              }`}
                            />
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <p className="font-medium">{contact.name}</p>
                              {contact.type !== "additional" && (
                                <Badge variant="outline" className="text-xs">
                                  {contact.type === "client" ? "Client" : "Location"}
                                </Badge>
                              )}
                            </div>
                            {contact.title && (
                              <p className="text-sm text-gray-500">{contact.title}</p>
                            )}
                            <div className="mt-1 flex items-center gap-3">
                              {contact.email && (
                                <a
                                  href={`mailto:${contact.email}`}
                                  className="text-sm text-blue-600 hover:underline"
                                >
                                  {contact.email}
                                </a>
                              )}
                              {contact.phone && (
                                <span className="text-sm text-gray-600">{contact.phone}</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => handleEditContact(contact)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          {contact.type === "additional" && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              onClick={() => handleDeleteContact(contact)}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Upcoming Tasks</CardTitle>
              </CardHeader>
              <CardContent>
                {tasks.length === 0 ? (
                  <div className="py-4 text-center">
                    <Calendar className="mx-auto mb-2 h-8 w-8 text-gray-400" />
                    <p className="text-sm text-gray-500">No upcoming tasks</p>
                  </div>
                ) : (
                  <>
                    <ul className="space-y-3">
                      {tasks
                        .filter((task: any) => task.status !== "completed")
                        .sort(
                          (a: any, b: any) =>
                            new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
                        )
                        .slice(0, 3)
                        .map((task: any) => {
                          const isOverdue = new Date(task.dueDate) < new Date();
                          return (
                            <li
                              key={task._id}
                              className={`flex items-center space-x-3 rounded-md border p-2 ${
                                isOverdue ? "border-red-200 bg-red-50" : ""
                              }`}
                            >
                              <div
                                className={`rounded-full p-2 ${
                                  isOverdue
                                    ? "bg-red-100"
                                    : task.status === "in-progress"
                                      ? "bg-blue-100"
                                      : "bg-gray-100"
                                }`}
                              >
                                {isOverdue ? (
                                  <AlertTriangle className="h-5 w-5 text-red-700" />
                                ) : (
                                  <Calendar className="h-5 w-5 text-blue-700" />
                                )}
                              </div>
                              <div className="flex-1">
                                <p className={`font-medium ${isOverdue ? "text-red-700" : ""}`}>
                                  {task.title}
                                </p>
                                <p
                                  className={`text-sm ${isOverdue ? "text-red-600" : "text-gray-500"}`}
                                >
                                  Due: {task.dueDate} {isOverdue && "(Overdue)"}
                                </p>
                                <p className="text-xs text-gray-500">
                                  Assigned to: {task.assignedUserName}
                                </p>
                              </div>
                            </li>
                          );
                        })}
                    </ul>
                    {tasks.filter((task: any) => task.status !== "completed").length > 3 && (
                      <Button
                        variant="outline"
                        className="mt-4 w-full"
                        onClick={() => setActiveTab("tasks")}
                      >
                        View All Tasks (
                        {tasks.filter((task: any) => task.status !== "completed").length})
                      </Button>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Task Management Sheets */}
      <AddOnboardingTaskSheet
        isOpen={isAddTaskSheetOpen}
        onClose={() => setIsAddTaskSheetOpen(false)}
        onboardingId={id as Id<"clientOnboarding">}
        onTaskAdded={() => {
          // Tasks will automatically refresh due to Convex reactivity
        }}
      />

      {selectedTask && (
        <EditOnboardingTaskSheet
          isOpen={isEditTaskSheetOpen}
          onClose={() => {
            setIsEditTaskSheetOpen(false);
            setSelectedTask(null);
          }}
          task={selectedTask}
          onTaskUpdated={() => {
            // Tasks will automatically refresh due to Convex reactivity
            toast.success("Task updated successfully!");
          }}
        />
      )}

      {/* Edit Onboarding Sheet */}
      <EditClientOnboardingSheet
        open={isEditOnboardingSheetOpen}
        onOpenChange={setIsEditOnboardingSheetOpen}
        onboarding={onboarding}
        onUpdated={() => {
          // Onboarding data will automatically refresh due to Convex reactivity
          toast.success("Onboarding updated successfully!");
        }}
      />

      {/* Contact Management Sheets */}
      <AddOnboardingContactSheet
        open={isAddContactSheetOpen}
        onOpenChange={setIsAddContactSheetOpen}
        onboardingId={id as Id<"clientOnboarding">}
        onContactAdded={() => {
          // Contacts will automatically refresh due to Convex reactivity
        }}
      />

      {selectedContact && (
        <EditOnboardingContactSheet
          open={isEditContactSheetOpen}
          onOpenChange={(open) => {
            setIsEditContactSheetOpen(open);
            if (!open) setSelectedContact(null);
          }}
          contact={selectedContact}
          onContactUpdated={() => {
            // Contacts will automatically refresh due to Convex reactivity
            toast.success("Contact updated successfully!");
          }}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to delete?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete "{taskToDelete?.title}" and
              remove it from our servers. The onboarding progress will be recalculated
              automatically.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDeleteTask}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
